import { api } from '@/shared/lib/api';

export interface WhatsappGroup {
  id: string;
  groupJid: string;
  name: string;
  clientId?: string | null;
  isActive: boolean;
  lastMessageAt?: string | null;
  lastClientMsgAt?: string | null;
  lastTeamMsgAt?: string | null;
  status?: 'OK' | 'AWAITING_REPLY' | 'INACTIVE' | 'CRITICAL';
  messageCount?: number;
  avgResponseTime?: number | null;
  clientName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WhatsappMessage {
  id: string;
  groupJid: string;
  messageWid: string;
  senderNumber: string;
  senderName: string;
  senderType: 'CLIENT' | 'TEAM';
  messageText?: string | null;
  messageType: 'TEXT' | 'IMAGE' | 'AUDIO' | 'VIDEO' | 'DOCUMENT' | 'OTHER';
  timestamp: string;
  createdAt: string;
}

export interface WhatsappOperationAnalysis {
  id: string;
  analysisDate: string;
  summary: string;
  sentiment: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE';
  status: 'OK' | 'ATTENTION' | 'CRITICAL';
  teamScore: number;
  totalMessages: number;
  clientMessages: number;
  teamMessages: number;
  avgResponseTime?: number | null;
  rankings: {
    criticalGroups: Array<{ jid: string; name: string; reason: string }>;
    attentionGroups: Array<{ jid: string; name: string; reason: string }>;
    topPerformers: Array<{ jid: string; name: string; reason: string }>;
  };
  problemTypes: Array<{
    type: string;
    count: number;
    description: string;
    affectedGroups: Array<{ jid: string; name: string; example: string }>;
  }>;
  metrics: {
    groupsCount: number;
    activeGroupsCount: number;
    globalAvgResponseTime?: number | null;
    teamPerformance?: {
      highlights: string[];
      improvements: string[];
    };
  };
  createdAt: string;
}

interface ApiResponse<T> {
  data: {
    success: boolean;
    data: T;
  };
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface OperationChatRequest {
  message: string;
  conversationHistory?: ChatMessage[];
}

export interface OperationChatResponse {
  response: string;
  timestamp: Date;
}

export const whatsappGroupsApi = {
  getGroups: async (filters?: {
    status?: string;
    clientId?: string;
    search?: string;
    periodDays?: number;
  }): Promise<ApiResponse<WhatsappGroup[]>> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.clientId) params.append('clientId', filters.clientId);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.periodDays) params.append('periodDays', filters.periodDays.toString());

    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/whatsapp/groups${query}`);
  },

  getGroup: async (groupJid: string): Promise<ApiResponse<WhatsappGroup>> => {
    return api.get(`/whatsapp/groups/${groupJid}`);
  },

  getGroupMessages: async (
    groupJid: string,
    filters?: {
      limit?: number;
      offset?: number;
    }
  ): Promise<ApiResponse<WhatsappMessage[]>> => {
    const params = new URLSearchParams();
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/whatsapp/groups/${groupJid}/messages${query}`);
  },

  getLastOperationAnalysis: async (): Promise<ApiResponse<WhatsappOperationAnalysis>> => {
    return api.get('/whatsapp/operation/analysis/last');
  },

  getOperationAnalysisHistory: async (limit = 30): Promise<ApiResponse<WhatsappOperationAnalysis[]>> => {
    return api.get(`/whatsapp/operation/analysis/history?limit=${limit}`);
  },

  triggerOperationAnalysis: async (): Promise<void> => {
    await api.post('/whatsapp/operation/analysis/trigger');
  },

  linkClient: async (groupJid: string, clientId: string): Promise<void> => {
    await api.post(`/whatsapp/groups/${groupJid}/link-client`, { clientId });
  },

  syncGroups: async (): Promise<void> => {
    await api.post('/whatsapp/sync');
  },

  sendChatMessage: async (request: OperationChatRequest): Promise<ApiResponse<OperationChatResponse>> => {
    return api.post('/whatsapp/operation/chat', request);
  },
};
